// Mirrors backend/modules/start-import/start-import.intent.js.
// The file only *suggests*; the operator always sees the question and picks.
export const INTENTS = Object.freeze({
  PASSENGERS: "passengers",
  PASSENGERS_ROUTES: "passengers_routes",
  ROUTES_ONLY: "routes_only",
});

export const EXISTING_MODES = Object.freeze({
  SKIP: "skip",
  UPDATE: "update",
  ADD: "add",
});

export const intentSignals = (extraction, matchSummary) => {
  const stats = extraction?.stats || {};
  const passengerCount = Number(stats.passengerCount) || 0;
  const namedRouteCount = Number(stats.namedRouteCount) || 0;
  const routeListCount = Number(stats.routeListCount) || 0;
  const existing = Number(matchSummary?.existing) || 0;
  return {
    passengerCount,
    namedRouteCount,
    distinctRouteNameCount: Number(stats.distinctRouteNameCount) || 0,
    routeListCount,
    routeListWithoutPassengers: Number(stats.routeListWithoutPassengers) || 0,
    hasRouteAssignments: namedRouteCount > 0,
    hasRouteList: routeListCount > 0,
    existingCount: existing,
    possibleCount: Number(matchSummary?.possible) || 0,
    mostlyRegistered: passengerCount > 0 && existing >= Math.ceil(passengerCount * 0.8),
  };
};

export const availableIntents = (signals) => {
  const options = [];
  if (signals.passengerCount > 0) options.push(INTENTS.PASSENGERS, INTENTS.PASSENGERS_ROUTES);
  if (signals.hasRouteAssignments || signals.hasRouteList) options.push(INTENTS.ROUTES_ONLY);
  return [...new Set(options)];
};

export const suggestIntent = (extraction, matchSummary) => {
  const signals = intentSignals(extraction, matchSummary);
  const options = availableIntents(signals);
  if (!options.length) return { suggested: null, options, signals, reason: "No passengers or services were found." };
  let suggested;
  let reason;
  if (signals.passengerCount === 0 && signals.hasRouteList) {
    suggested = INTENTS.ROUTES_ONLY;
    reason = "This file lists services but no passengers.";
  } else if (signals.mostlyRegistered && signals.hasRouteAssignments) {
    suggested = INTENTS.ROUTES_ONLY;
    reason = "Almost everyone here is already registered and the file says which service they ride.";
  } else if (signals.hasRouteAssignments) {
    suggested = INTENTS.PASSENGERS_ROUTES;
    reason = "The file already says which service each passenger rides.";
  } else {
    suggested = INTENTS.PASSENGERS;
    reason = "The file has passengers but no service assignments.";
  }
  if (!options.includes(suggested)) suggested = options[0];
  return { suggested, options, signals, reason };
};

export const SERVICE_DIRECTIONS = Object.freeze({ BOTH: "both", PICKUP: "pickup", DROPOFF: "dropoff" });

// Morning pick-up, afternoon drop-off, or both — proposed from the columns / list rows the file has.
export const suggestServiceDirection = (extraction) => {
  const stats = extraction?.stats || {};
  const pickup = (Number(stats.pickupAssignedCount) || 0) + (Number(stats.routeListPickupCount) || 0);
  const dropoff = (Number(stats.dropoffAssignedCount) || 0) + (Number(stats.routeListDropoffCount) || 0);
  if (pickup > 0 && dropoff > 0) {
    return { suggested: SERVICE_DIRECTIONS.BOTH, reason: "The file names both a morning and an afternoon service.", fromFile: true };
  }
  if (pickup > 0) return { suggested: SERVICE_DIRECTIONS.PICKUP, reason: "The file only names morning services.", fromFile: true };
  if (dropoff > 0) return { suggested: SERVICE_DIRECTIONS.DROPOFF, reason: "The file only names afternoon services.", fromFile: true };
  return { suggested: SERVICE_DIRECTIONS.BOTH, reason: "The file does not say which direction, so both are proposed.", fromFile: false };
};

export const directionCopy = (direction) => {
  switch (direction) {
    case SERVICE_DIRECTIONS.PICKUP:
      return { title: "Morning pick-up only", detail: "One service per group that brings passengers to school." };
    case SERVICE_DIRECTIONS.DROPOFF:
      return { title: "Afternoon drop-off only", detail: "One service per group that takes passengers home." };
    default:
      return { title: "Morning pick-up and afternoon drop-off", detail: "Two services per group — the usual school day." };
  }
};

export const directionFlags = (direction) => ({
  createPickup: direction !== SERVICE_DIRECTIONS.DROPOFF,
  createDropoff: direction !== SERVICE_DIRECTIONS.PICKUP,
});

export const intentCreatesRoutes = (intent) => intent === INTENTS.PASSENGERS_ROUTES || intent === INTENTS.ROUTES_ONLY;
export const intentCreatesPassengers = (intent) => intent === INTENTS.PASSENGERS || intent === INTENTS.PASSENGERS_ROUTES;

export const intentCopy = (intent, signals = {}) => {
  const routeWord = signals.distinctRouteNameCount > 0 ? "the services named in the file" : "services grouped by capacity";
  switch (intent) {
    case INTENTS.PASSENGERS:
      return {
        title: "Save the passengers",
        detail: "Students and parents are added to your operation. No services are created — you can build them any time.",
      };
    case INTENTS.PASSENGERS_ROUTES:
      return {
        title: "Save the passengers and draft services",
        detail: `Students and parents are added, then we draft ${routeWord} for you to review before anything is created.`,
      };
    case INTENTS.ROUTES_ONLY:
      return {
        title: "Only build services",
        detail:
          signals.passengerCount > 0
            ? "Passengers here are already registered. We place them on the services the file names, without creating new students."
            : "Create the services listed in this file so you can assign passengers to them later.",
      };
    default:
      return { title: "", detail: "" };
  }
};

export const existingModeCopy = (mode) => {
  switch (mode) {
    case EXISTING_MODES.UPDATE:
      return { title: "Refresh their details", detail: "Address, phone, class, and parents are updated from the file. Nothing is duplicated." };
    case EXISTING_MODES.ADD:
      return { title: "Add them again", detail: "A second record is created. Choose this only if these are different people with the same name." };
    default:
      return { title: "Leave them as they are", detail: "Registered students are skipped. Only new names are added." };
  }
};

// What will the commit do, row by row, given the operator's choices? Mirrors backend decideRowAction.
export const planSummary = ({ extraction, match, intent, existingMode, existingDecisions = {} }) => {
  const named = (extraction?.passengers || []).filter((passenger) => passenger.name);
  const matches = match?.matches || {};
  const createPassengers = intentCreatesPassengers(intent);
  const summary = { add: 0, update: 0, skip: 0, link: 0, unmatched: 0, total: named.length };
  named.forEach((passenger) => {
    const entry = matches[passenger.rowNumber];
    const decided = existingDecisions[String(passenger.rowNumber)]?.action || null;
    if (!createPassengers) {
      if (entry && decided !== "add" && decided !== "skip") summary.link += 1;
      else if (entry && decided === "skip") summary.skip += 1;
      else summary.unmatched += 1;
      return;
    }
    if (decided === "add" || !entry) {
      summary.add += 1;
      return;
    }
    if (decided === "update") {
      summary.update += 1;
      return;
    }
    if (decided === "skip" || decided === "link") {
      summary.skip += 1;
      return;
    }
    if (entry.confidence !== "high") {
      summary.add += 1;
      return;
    }
    if (existingMode === EXISTING_MODES.UPDATE) summary.update += 1;
    else if (existingMode === EXISTING_MODES.ADD) summary.add += 1;
    else summary.skip += 1;
  });
  return summary;
};

// Which stages does this journey need? Passengers-only skips the route screens entirely.
export const stagesForJourney = ({ source, intent }) => {
  // The sample is a known, complete roster: it goes straight to being written.
  if (source === "sample") return ["welcome", "continue"];
  if (source !== "upload") return ["welcome", "understand", "prepare", "visualize", "apps", "continue"];
  if (intent === INTENTS.PASSENGERS) return ["welcome", "understand", "continue"];
  return ["welcome", "understand", "prepare", "visualize", "continue"];
};
